import * as React from 'react';

/**
 * Debounce a value: returns `value` once it has stayed unchanged for
 * `delayMs` milliseconds (each change restarts the timer). The first render
 * returns the initial value immediately.
 *
 * Typical use: drive a server-side search from a text input without one
 * request per keystroke.
 *
 * @param value - The value to debounce (compared with `Object.is`)
 * @param delayMs - Quiet period before the value is published
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);

  return debounced;
}
